﻿import { DropDownInput } from 'components/uikit/inputs/dropdown/DropDownInput.tsx';
import { Field } from 'components/uikit/Field.tsx';
import {
  FC,
  HTMLAttributes,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useScopedTranslation } from 'application/localization/useScopedTranslation.ts';
import { QueryFactory } from 'services/api';
import { SortableTree } from 'dnd-kit-sortable-tree';
import { SignerListItem, SignerListItemProps } from './SignerListItem.tsx';
import { SignerDto } from 'services/api/api-client.types.ts';
import { Box } from '@mui/material';
import { TextButton } from 'components/uikit/buttons/TextButton.tsx';

import styles from './SignersModalBlock.module.scss';
import DeleteIcon from 'assets/icons/delete.svg?react';
import EditIcon from 'assets/icons/edit.svg?react';
import { EditSignerModal } from '../addEditSignerModals/EditSignerModal.tsx';
import { AddSignerModal } from '../addEditSignerModals/AddSignerModal.tsx';
import { DeleteSignerModal } from '../deleteSignerModal/DeleteSignerModal.tsx';
import { Loading } from 'components/uikit/suspense/Loading.tsx';
import { Input } from 'components/uikit/inputs/Input.tsx';

export type SignersModalBlockProps = {
  projectRoleId?: number;
  onUpdate: (signerIdList: number[]) => void;
};

enum DropdownSignerOptionType {
  Button,
  Option,
}

type DropdownSignerOption =
  | {
      type: DropdownSignerOptionType.Button;
      text: string;
      action: () => void;
    }
  | {
      type: DropdownSignerOptionType.Option;
      dto: SignerDto;
      onEdit: (e: React.MouseEvent<HTMLElement>) => void;
      onDelete: (e: React.MouseEvent<HTMLElement>) => void;
    };

export const SignersModalBlock: FC<SignersModalBlockProps> = (props) => {
  const i18n = useScopedTranslation('Component.SignersModalBlock');
  const [signerList, setSignerList] = useState<SignerDto[]>([]);
  useEffect(() => {
    props.onUpdate(signerList.map((e) => e.id));
  }, [signerList]);

  const [isAddSignerModalOpen, setIsAddSignerModalOpen] = useState(false);
  const [isEditSignerModalOpen, setIsEditSignerModalOpen] = useState(false);
  const [isDeleteSignerModalOpen, setIsDeleteSignerModalOpen] = useState(false);
  const [signerModalId, setSignerModalId] = useState(0);

  const [isSignerListSet, setIsSignerListSet] = useState(false);
  const projectSignerQuery = QueryFactory.ProjectSignerQuery.useSearchQuery(
    {
      projectId: null,
      projectRoleId: props.projectRoleId,
    },
    {
      enabled: !!props.projectRoleId,
    },
  );

  useEffect(() => {
    if (!isSignerListSet && projectSignerQuery.data) {
      setSignerList(projectSignerQuery.data.data.map((e) => e.signer));
      setIsSignerListSet(true);
    }
  }, []);

  const signersQuery = QueryFactory.SignerQuery.useSearchQuery();

  const onSignerDelete = (id: number) => {
    setSignerList(signerList.filter((e) => e.id != id));
  };

  const onSignerEdit = (id: number) => {
    setSignerModalId(id);
    setIsEditSignerModalOpen(true);
  };

  const treeSigners = signerList.map((signer) => {
    return {
      id: signer.id,
      signer: signer,
      canHaveChildren: false,
      onDelete: onSignerDelete,
      onEdit: onSignerEdit,
    } as SignerListItemProps;
  });

  const dropdownSignersValueOptions = useMemo(() => {
    const newSigner: DropdownSignerOption = {
      type: DropdownSignerOptionType.Button,
      text: i18n.t('new_signer_dropdown_button'),
      action: () => {
        setIsAddSignerModalOpen(true);
      },
    };

    const options: DropdownSignerOption[] =
      signersQuery.data?.data
        .filter((dto) => {
          return !signerList.map((s) => s.id).includes(dto.id);
        })
        .map((e) => {
          return {
            type: DropdownSignerOptionType.Option,
            dto: e,
            onEdit: (event) => {
              event.stopPropagation();
              setSignerModalId(e.id);
              setIsEditSignerModalOpen(true);
            },
            onDelete: (event) => {
              event.stopPropagation();
              setSignerModalId(e.id);
              setIsDeleteSignerModalOpen(true);
            },
          };
        }) ?? [];

    return [newSigner, ...options];
  }, [signersQuery.data, signerList]);

  return (
    <Loading
      loading={
        signersQuery.isLoading ||
        (!!props.projectRoleId && projectSignerQuery.isLoading)
      }
    >
      <Field
        title={i18n.t('signers_dropdown')}
        className={styles.dropDownField}
      >
        <DropDownInput
          testId={'signers-dropdown-input'}
          onValueChanged={async (newSelectedOption, _) => {
            if (newSelectedOption === null) {
              return;
            }
            if (newSelectedOption.type == DropdownSignerOptionType.Button) {
              newSelectedOption.action();
            } else {
              setSignerList([...signerList, newSelectedOption.dto]);
            }
          }}
          enableSearch={true}
          options={dropdownSignersValueOptions}
          value={null}
          getOptionLabel={getLabelFromDropdownSignerOption}
          renderOption={getElementFromDropdownSignerOption}
          renderInput={(params) => <Input {...params} />}
          placeholder={''}
        />
      </Field>
      {treeSigners.length > 0 && (
        <div className={styles.signersList}>
          <div className={styles.not_scrollable}>
            <SortableTree
              items={treeSigners}
              onItemsChanged={(items) => {
                setSignerList(items.map((e) => e.signer));
              }}
              TreeItemComponent={SignerListItem}
            />
          </div>
        </div>
      )}
      <AddSignerModal
        isOpen={isAddSignerModalOpen}
        onClose={() => {
          setIsAddSignerModalOpen(false);
        }}
      />
      <EditSignerModal
        isOpen={isEditSignerModalOpen}
        signerId={signerModalId}
        onClose={() => {
          setSignerModalId(0);
          setIsEditSignerModalOpen(false);
        }}
        onUpdated={(updatedSigner) => {
          setSignerList((prev) =>
            prev.map((signer) =>
              signer.id === updatedSigner.id ? updatedSigner : signer,
            ),
          );
        }}
      />
      <DeleteSignerModal
        isOpen={isDeleteSignerModalOpen}
        signerId={signerModalId}
        onClose={() => {
          setSignerModalId(0);
          setIsDeleteSignerModalOpen(false);
        }}
      />
    </Loading>
  );

  function getElementFromDropdownSignerOption(
    props: HTMLAttributes<HTMLLIElement>,
    option: DropdownSignerOption,
  ): ReactNode {
    if (!option) {
      return <></>;
    }
    if (option.type == DropdownSignerOptionType.Button) {
      return (
        <Box
          {...props}
          data-test-id={'signers-dropdown-button'}
          component="li"
          key={option.text}
          className={styles.dropdownButton}
        >
          {option.text}
        </Box>
      );
    } else {
      return (
        <Box
          {...props}
          data-test-id={'signers-dropdown-option'}
          component="li"
          sx={{
            display: 'flex !important',
            justifyContent: 'space-between !important',
            paddingRight: '4px !important',
          }}
          key={option.dto.id}
          className={styles.dropdownOption}
        >
          <span className={styles.dropdownOptionLabel}>
            <div className={styles.name}>
              {option.dto.firstName} {option.dto.lastName}
            </div>
            <div className={styles.email}>{option.dto.email}</div>
          </span>
          <div className={styles.dropdownOptionButtons}>
            {option.dto.canBeModified && (
              <TextButton
                testId={'edit-button'}
                icon={<EditIcon />}
                className={styles.editButton}
                onClick={option.onEdit}
              />
            )}
            <TextButton
              testId={'delete-button'}
              icon={<DeleteIcon />}
              className={styles.deleteButton}
              onClick={option.onDelete}
            />
          </div>
        </Box>
      );
    }
  }

  function getLabelFromDropdownSignerOption(
    option: DropdownSignerOption,
  ): string {
    if (!option) {
      return '';
    }
    if (option.type == DropdownSignerOptionType.Button) {
      return option.text;
    } else {
      return `${option.dto.firstName} ${option.dto.lastName}`;
    }
  }
};
